// Every design-tool file for every mission, keyed by path under /mds/.
import { missions, themeFor, type Mission } from '../content/missions.ts'
import { roleNames, type ThemePair } from '../content/theme.ts'
import { ase } from './ase.ts'
import { dtcgModes, penpotTokens } from './tokens.ts'
import { sketchLibrary, type SketchTextStyle } from './sketch.ts'

/** The type scale as Sketch text styles, by PostScript name. */
export const sketchTextStyles = (ink: string): SketchTextStyle[] => [
  { name: 'Display', font: 'InstrumentSerif-Regular', size: 72, lineHeight: 72, color: ink },
  { name: 'Heading 1', font: 'InstrumentSerif-Regular', size: 56, lineHeight: 57, color: ink },
  { name: 'Heading 2', font: 'InstrumentSerif-Regular', size: 40, lineHeight: 43, color: ink },
  { name: 'Heading 3', font: 'Inter-SemiBold', size: 24, lineHeight: 29, color: ink },
  { name: 'Lead', font: 'Inter-Regular', size: 22, lineHeight: 33, color: ink },
  { name: 'Body', font: 'Inter-Regular', size: 16, lineHeight: 25, color: ink },
  { name: 'Body strong', font: 'Inter-SemiBold', size: 16, lineHeight: 25, color: ink },
  { name: 'Small', font: 'Inter-Regular', size: 14, lineHeight: 21, color: ink },
  { name: 'Code', font: 'GeistMono-Regular', size: 14, lineHeight: 21, color: ink },
]

/** Light and dark swatch groups, named so they read well in a Swatches panel. */
export const swatches = (theme: ThemePair, label: string) =>
  ase(
    (['light', 'dark'] as const).map((mode) => ({
      name: `${label} ${mode}`,
      colors: roleNames.map((name) => ({ name, hex: theme[mode][name] })),
    })),
  )

export function designToolFiles(list: Mission[] = missions): Record<string, string | Uint8Array> {
  return Object.fromEntries(
    list.flatMap((mission) => {
      const theme = themeFor(mission)
      const modes = dtcgModes(theme, mission.name)
      return [
        [`tokens/${mission.id}/light.tokens.json`, modes.light],
        [`tokens/${mission.id}/dark.tokens.json`, modes.dark],
        [`tokens/${mission.id}/penpot.tokens.json`, penpotTokens(theme, mission.name)],
        [`swatches/${mission.id}.ase`, swatches(theme, mission.short)],
        [`sketch/${mission.id}.sketch`, sketchLibrary(theme, mission.name, sketchTextStyles(theme.light.ink))],
      ]
    }),
  )
}
